import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  injectMutation,
  injectQuery,
  QueryClient,
} from '@tanstack/angular-query-experimental';
import { lastValueFrom, take } from 'rxjs';
import { auth as AuthGen } from '@valvur/shared-grpc';
import { environment } from '../../../../environments/environment';
import { AuthSessionService } from '../../auth/services/auth-session.service';

@Injectable({ providedIn: 'root' })
export class FamilyHubService {
  #http = inject(HttpClient);
  #session = inject(AuthSessionService);
  #queryClient = inject(QueryClient);

  familyQuery = injectQuery(() => ({
    queryKey: ['family', 'hub'],
    retry: 1,
    enabled: this.#session.isAuthenticated(),
    queryFn: () =>
      lastValueFrom(
        this.#http
          .get<AuthGen.Family>(`${environment.baseUrl}/user/family`)
          .pipe(take(1)),
      ),
  }));

  invitesQuery = injectQuery(() => ({
    queryKey: ['family', 'invites'],
    retry: 1,
    enabled: this.#session.isAuthenticated(),
    queryFn: () =>
      lastValueFrom(
        this.#http
          .get<AuthGen.FamilyInvitesList>(
            `${environment.baseUrl}/user/family/co-parent-invites/mine`,
          )
          .pipe(take(1)),
      ),
  }));

  updateParentalFigure = injectMutation(() => ({
    mutationFn: (parentalFigure: string) =>
      lastValueFrom(
        this.#http
          .patch<AuthGen.FamilyMember>(
            `${environment.baseUrl}/user/family/me`,
            { parentalFigure },
          )
          .pipe(take(1)),
      ),
    onSuccess: async () => {
      await this.#queryClient.invalidateQueries({ queryKey: ['family'] });
      await this.#queryClient.invalidateQueries({ queryKey: ['user'] });
    },
  }));

  inviteCoParent = injectMutation(() => ({
    mutationFn: () =>
      lastValueFrom(
        this.#http
          .post<AuthGen.CoParentInviteResult>(
            `${environment.baseUrl}/user/family/co-parent-invites`,
            {},
          )
          .pipe(take(1)),
      ),
    onSuccess: async () => {
      await this.#queryClient.invalidateQueries({
        queryKey: ['family', 'invites'],
      });
    },
  }));

  inviteCaregiver = injectMutation(() => ({
    mutationFn: () =>
      lastValueFrom(
        this.#http
          .post<AuthGen.CoParentInviteResult>(
            `${environment.baseUrl}/user/family/caregiver-invites`,
            {},
          )
          .pipe(take(1)),
      ),
    onSuccess: async () => {
      await this.#queryClient.invalidateQueries({
        queryKey: ['family', 'invites'],
      });
    },
  }));

  acceptInvite = injectMutation(() => ({
    mutationFn: (dto: AuthGen.CoParentInviteTokenDto) =>
      lastValueFrom(
        this.#http
          .post<AuthGen.Family>(
            `${environment.baseUrl}/user/family/co-parent-invites/accept`,
            dto,
          )
          .pipe(take(1)),
      ),
    onSuccess: async () => {
      await this.#queryClient.invalidateQueries({ queryKey: ['family'] });
      await this.#queryClient.invalidateQueries({ queryKey: ['user'] });
    },
  }));

  acceptCaregiverInvite = injectMutation(() => ({
    mutationFn: (dto: AuthGen.CoParentInviteTokenDto) =>
      lastValueFrom(
        this.#http
          .post<AuthGen.Family>(
            `${environment.baseUrl}/user/family/caregiver-invites/accept`,
            dto,
          )
          .pipe(take(1)),
      ),
    onSuccess: async () => {
      await this.#queryClient.invalidateQueries({ queryKey: ['family'] });
      await this.#queryClient.invalidateQueries({ queryKey: ['user'] });
    },
  }));

  rejectInvite = injectMutation(() => ({
    mutationFn: (dto: AuthGen.CoParentInviteTokenDto) =>
      lastValueFrom(
        this.#http
          .post<AuthGen.CoParentInvite>(
            `${environment.baseUrl}/user/family/co-parent-invites/reject`,
            dto,
          )
          .pipe(take(1)),
      ),
    onSuccess: async () => {
      await this.#queryClient.invalidateQueries({
        queryKey: ['family', 'invites'],
      });
    },
  }));

  removeCoParent = injectMutation(() => ({
    mutationFn: (targetUserId: string) =>
      lastValueFrom(
        this.#http
          .delete<AuthGen.Family>(
            `${environment.baseUrl}/user/family/co-parents/${encodeURIComponent(targetUserId)}`,
          )
          .pipe(take(1)),
      ),
    onSuccess: async () => {
      await this.#queryClient.invalidateQueries({ queryKey: ['family'] });
      await this.#queryClient.invalidateQueries({ queryKey: ['user'] });
    },
  }));

  leaveFamily = injectMutation(() => ({
    mutationFn: () =>
      lastValueFrom(
        this.#http
          .post<AuthGen.Family>(`${environment.baseUrl}/user/family/leave`, {})
          .pipe(take(1)),
      ),
    onSuccess: async () => {
      await this.#queryClient.invalidateQueries({ queryKey: ['family'] });
      await this.#queryClient.invalidateQueries({ queryKey: ['user'] });
    },
  }));

  getInvitePreview(token: string): Promise<AuthGen.CoParentInvite> {
    return lastValueFrom(
      this.#http
        .get<AuthGen.CoParentInvite>(
          `${environment.baseUrl}/user/family/co-parent-invites/by-token/${encodeURIComponent(token)}`,
        )
        .pipe(take(1)),
    );
  }

  /** Public claim when authenticated viewer opens the invite (sets claimedByUserId). */
  async claimInviteByToken(token: string): Promise<void> {
    const trimmed = token.trim();
    if (!trimmed || !this.#session.isAuthenticated()) {
      return;
    }
    try {
      await this.getInvitePreview(trimmed);
    } catch {
      /* ignore — invite may be invalid/expired */
    }
  }

  listMyInvites(): Promise<AuthGen.FamilyInvitesList> {
    return lastValueFrom(
      this.#http
        .get<AuthGen.FamilyInvitesList>(
          `${environment.baseUrl}/user/family/co-parent-invites/mine`,
        )
        .pipe(take(1)),
    );
  }

  /** First pending incoming invite claimed by this user (if any). */
  async findPendingIncomingInvite(): Promise<AuthGen.CoParentInvite | null> {
    try {
      const list = await this.listMyInvites();
      return (
        list.incoming?.find((inv) => inv.status === 'pending') ?? null
      );
    } catch {
      return null;
    }
  }
}
